import { useState, useEffect, useRef } from 'react';
import { LuSun, LuMoon, LuX, LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { Tabs, TabsList, TabsTrigger, TabsPanel } from '@/components/ui/tabs';

import { Options } from './Options';
import { Question } from './Question';
import { Checklist } from './Checklist';
import { ConditionChecker, Evaluator } from './Eval';
import { GraphAssertions } from './Graph';
import { Queue } from './Queue';
import { Report } from './Report';
import { Scenarios } from './Scenarios';
import { Version } from './Version';
import { OnOffboarding } from './OnOffboarding';
import { useDebugTheme } from './useDebugTheme';
import { useDebugMode } from '../../context/appContext';

const TAB_VALUES = [
  'version',
  'options',
  'question',
  'queue',
  'report',
  'checklist',
  'eval',
  'graph',
  'onoffboarding',
] as const;

const TAB_LABELS = [
  'Version',
  'Options',
  'Question',
  'Queue',
  'Report',
  'Checklist',
  'Eval',
  'Graph',
  'On/offboarding',
];

export const Debug = () => {
  const [isScenariosOpen, setIsScenariosOpen] = useState(
    sessionStorage.getItem('isScenariosOpen') === 'true',
  );
  const [scenarioHeight, setScenarioHeight] = useState(
    parseInt(sessionStorage.getItem('scenarioHeight') || '50', 10),
  );
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isDark, setThemeMode } = useDebugTheme();
  const [, setDebugMode] = useDebugMode();

  useEffect(() => {
    sessionStorage.setItem('isScenariosOpen', String(isScenariosOpen));
  }, [isScenariosOpen]);

  useEffect(() => {
    sessionStorage.setItem('scenarioHeight', String(scenarioHeight));
  }, [scenarioHeight]);

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const container = e.currentTarget.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const mouseY = e.clientY - containerRect.top;
    const newHeight = (mouseY / containerRect.height) * 100;

    const constrainedHeight = Math.max(20, Math.min(80, newHeight));
    setScenarioHeight(constrainedHeight);
  };

  const handleDocumentMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const mouseY = e.clientY - containerRect.top;
    const newHeight = 100 - (mouseY / containerRect.height) * 100;

    const constrainedHeight = Math.max(20, Math.min(80, newHeight));
    setScenarioHeight(constrainedHeight);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className={`h-dvh max-h-dvh flex flex-col bg-[var(--dbg-bg)] text-[var(--dbg-text)] ${
        isScenariosOpen ? '' : 'pb-10'
      }`}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Debug toolbar */}
      <div
        className="flex items-center justify-end px-2 h-9 border-b shrink-0"
        style={{
          backgroundColor: 'var(--dbg-bg-secondary)',
          borderColor: 'var(--dbg-border)',
        }}
      >
        <button
          onClick={() => setThemeMode(isDark ? 'light' : 'dark')}
          className="p-1 text-[var(--dbg-text-secondary)] hover:text-[var(--dbg-text)] cursor-pointer"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? (
            <LuSun className="size-4" />
          ) : (
            <LuMoon className="size-4" />
          )}
        </button>
        <button
          onClick={() => setDebugMode(false)}
          className="p-1 text-[var(--dbg-text-secondary)] hover:text-[var(--dbg-text)] cursor-pointer"
          title="Close debug panel"
        >
          <LuX className="size-4" />
        </button>
      </div>

      {/* Main content area */}
      <div
        className="flex flex-col border-b border-[var(--dbg-border)] overflow-hidden"
        style={{
          flexBasis: isScenariosOpen
            ? `${100 - scenarioHeight}%`
            : 'calc(100% - 3rem)',
          minHeight: isScenariosOpen ? '20%' : 'calc(100% - 3rem)',
        }}
      >
        <Tabs defaultValue="version" className="flex-1 min-h-0 overflow-hidden">
          <TabsList
            className="shrink-0"
            style={{ backgroundColor: 'var(--dbg-bg-secondary)' }}
          >
            {TAB_VALUES.map((value, i) => (
              <TabsTrigger
                key={value}
                value={value}
                className="text-[var(--dbg-text-secondary)] data-[selected]:text-[var(--dbg-accent)] data-[selected]:border-[var(--dbg-accent)]"
              >
                {TAB_LABELS[i]}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="flex-1 overflow-auto no-scrollbar">
            <TabsPanel value="version">
              <Version />
            </TabsPanel>
            <TabsPanel value="options">
              <Options />
            </TabsPanel>
            <TabsPanel value="question">
              <Question />
            </TabsPanel>
            <TabsPanel value="queue">
              <Queue />
            </TabsPanel>
            <TabsPanel value="report">
              <Report />
            </TabsPanel>
            <TabsPanel value="checklist">
              <Checklist />
            </TabsPanel>
            <TabsPanel value="eval">
              <ConditionChecker />
              <Evaluator />
            </TabsPanel>
            <TabsPanel value="graph">
              <GraphAssertions />
            </TabsPanel>
            <TabsPanel value="onoffboarding">
              <OnOffboarding />
            </TabsPanel>
          </div>
        </Tabs>
      </div>

      {/* Draggable Separator */}
      {isScenariosOpen && (
        <div
          className="h-1 bg-[var(--dbg-border)] cursor-row-resize hover:bg-blue-500 transition-colors"
          onMouseDown={handleMouseDown}
        />
      )}

      {/* Collapsible Scenarios Panel */}
      <div
        className="bg-[var(--dbg-bg)] border-t border-[var(--dbg-border)] transition-all duration-300 ease-in-out overflow-hidden"
        style={{
          height: isScenariosOpen ? `${scenarioHeight}%` : '3rem',
          minHeight: isScenariosOpen ? '20%' : '3rem',
        }}
      >
        {/* Scenarios Header - Always visible */}
        <div
          className="h-12 flex items-center justify-center cursor-pointer bg-[var(--dbg-bg-secondary)] hover:bg-[var(--dbg-hover)] border-b border-[var(--dbg-border)]"
          onClick={() => setIsScenariosOpen(!isScenariosOpen)}
        >
          <span className="text-sm font-medium text-[var(--dbg-text-muted)] flex items-center gap-1">
            Scenarios{' '}
            {isScenariosOpen ? (
              <LuChevronDown className="size-4 text-[var(--dbg-text-secondary)]" />
            ) : (
              <LuChevronUp className="size-4 text-[var(--dbg-text-secondary)]" />
            )}
          </span>
        </div>

        {/* Scenarios Content - Only visible when open */}
        {isScenariosOpen && (
          <div
            className="flex-1 overflow-y-auto overflow-x-hidden"
            style={{ height: 'calc(100% - 3rem)' }}
          >
            <Scenarios />
          </div>
        )}
      </div>
    </div>
  );
};
